import {
    ChangeEvent,
    KeyboardEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import NumberInput from "@/common/NumberInput";
import { Badges } from "@/components/DecimalInput/Badges";
import { BodyMetricLabel } from "@/components/DecimalInput/BodyMetricLabel";
import { FeetInchesInput } from "@/components/DecimalInput/FeetInchesInput";
import { logAnalyticsEvent, setAnalyticsUserProperties } from "@/constants/amplitude";
import {
    BodyMetricTypes,
    inputInfoByUnitType,
    sendBodyMetricsAnalytics,
    UnitTypes,
} from "@/constants/bodyMetrics";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { ExperimentIds } from "@/constants/experiments";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { useDeviceType } from "@/hooks/useDeviceType";
import { usePrefix } from "@/hooks/usePrefix";
import i18n from "@/localization/i18next";
import { HandleNext } from "@/types/common";
import { Question } from "@/types/question";
import { CURRENT_WEIGHT_STRING_KEY, TARGET_WEIGHT_STRING_KEY } from "@/utils/AnswersProvider";
import { useExperiment } from "@/utils/ExperimentsProvider";
import { validateHeight } from "@/utils/services/heightFormatter";
import {
    calculateWeight,
    convertToKg,
    kgToLbs,
    lbsToKg,
    validateWeight,
} from "@/utils/services/weightFormatters";

import Button from "../../common/Button";
import useAnswers from "../../utils/useAnswers";
import useUserDataConsent from "../UserConsent";

interface DecimalInputProps {
    question: Question;
    handleNext: HandleNext;
    isActive: boolean;
}

const validateAge = (value?: number | null) =>
    value && value < 18 ? i18n.t("age_restriction_error") : null;

const validationByInputType = {
    [BodyMetricTypes.WEIGHT]: validateWeight,
    [BodyMetricTypes.HEIGHT]: validateHeight,
    [BodyMetricTypes.AGE]: null,
};

const DecimalInput = memo<DecimalInputProps>(({ question, handleNext, isActive }) => {
    const { answers, setAnswers } = useAnswers();
    const unitType = (answers.units as UnitTypes) || UnitTypes.Imperial;
    const inputInfo = inputInfoByUnitType[unitType][question.inputType as BodyMetricTypes];
    const inputRef = useRef<HTMLInputElement | null>(null);
    const ftInchesInputRefs = useRef<HTMLInputElement[] | null[]>([]);
    const { isMobile, isAndroid } = useDeviceType();
    const { getEventType } = usePrefix();
    const inputClass = useMemo(() => `input-${question.id}`, [question.id]);

    const { isTest: isNewQsSequenceExp } = useExperiment({
        experimentId: ExperimentIds.NEW_QS_SEQUENCE,
    });

    const withConsent = isNewQsSequenceExp && question.id === QUESTIONS_IDS.HEIGHT;
    const { Component: ConsentComponent, isConsent } = useUserDataConsent({
        event: question.event,
    });
    const canProceed = (withConsent && isConsent) || !withConsent;

    const image = question.img;
    const isImperialHeight =
        question.inputType === BodyMetricTypes.HEIGHT && unitType === UnitTypes.Imperial;

    const getValidValue = useCallback(() => {
        const value = answers[question.id] as number;
        if (
            value &&
            question.inputType === BodyMetricTypes.WEIGHT &&
            unitType === UnitTypes.Imperial
        ) {
            return kgToLbs(value);
        }
        return value;
    }, [answers, question.id, question.inputType, unitType]);

    const initValue = useMemo(() => {
        return getValidValue();
    }, [getValidValue]);

    const [value, setValue] = useState<number | null>(initValue);
    const [ageError, setAgeError] = useState<string | null>(null);

    const checkIsAgeValid = (value: number | null) => {
        const validationResult = validateAge(value);
        setAgeError(validationResult);

        return !!validationResult;
    };

    const error = useMemo(() => {
        if (image) return null;
        return value
            ? validationByInputType[question.inputType as BodyMetricTypes]?.(value, unitType)
            : null;
    }, [value, question.inputType, unitType, image]);

    useEffect(() => {
        if (isActive) {
            if (question.inputType === BodyMetricTypes.WEIGHT && unitType === UnitTypes.Imperial) {
                setValue(getValidValue());
            }
        }
        // do not update dependencies here
        // this should be calculated only when isActive changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    const handleUnitsChange = (newUnit: UnitTypes) => {
        if (newUnit !== unitType) {
            setValue((value) => {
                if (question.inputType === BodyMetricTypes.WEIGHT) {
                    if (newUnit === UnitTypes.Imperial) {
                        return value && kgToLbs(value);
                    } else {
                        return value && lbsToKg(value);
                    }
                }
                return value;
            });
        }
    };

    const setStringWeight = useCallback(
        (value: number | null) => {
            if (value && question.id === QUESTIONS_IDS.TARGET_WEIGHT) {
                const weight = calculateWeight(value, unitType);
                setAnalyticsUserProperties({
                    true_target_weight: weight,
                });
                setAnswers((prev) => ({
                    ...prev,
                    [TARGET_WEIGHT_STRING_KEY]: weight,
                }));
            }

            if (value && question.id === QUESTIONS_IDS.CURRENT_WEIGHT) {
                const weight = calculateWeight(value, unitType);
                setAnswers((prev) => ({
                    ...prev,
                    [CURRENT_WEIGHT_STRING_KEY]: weight,
                }));
            }
        },
        [question.id, setAnswers, unitType]
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setValue(() => {
            const value = +e.target.value;

            return value || null;
        });
        setAgeError(null);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const isValidForm = value && !error;
        if (e.key === "Enter" && isValidForm && canProceed) {
            handleContinue();
            inputRef?.current?.blur();
            ftInchesInputRefs?.current[1]?.blur();
        }
    };

    const handleContinue = useCallback(() => {
        let targetValue = value;
        if (question.inputType === BodyMetricTypes.WEIGHT) {
            targetValue = value && convertToKg(value, unitType);
        }
        setStringWeight(targetValue);

        setAnswers((prev) => ({ ...prev, [question.id]: targetValue }));

        const isAgeError =
            question.inputType === BodyMetricTypes.AGE && checkIsAgeValid(targetValue);
        if (isAgeError) {
            logAnalyticsEvent(WebEventTypes.AGE_WARNING, {
                [WebEventProps.SCREEN]: getEventType(question.id),
            });
        }
        if (!isAgeError) {
            handleNext();
            sendBodyMetricsAnalytics(question.id, question.event, targetValue);
        }
    }, [
        value,
        question.inputType,
        question.id,
        question.event,
        setStringWeight,
        setAnswers,
        unitType,
        handleNext,
        getEventType,
    ]);

    const handleSkip = () => {
        setValue(null);
        handleNext({ [question.id]: null });
    };

    const { isTest: isSeniorsFunnelExp } = useExperiment({
        experimentId: ExperimentIds.SENIORS_FUNNEL,
    });

    const agePlaceholder = isSeniorsFunnelExp ? "53" : "25";

    const focusAndClick = (el: HTMLInputElement | null) => {
        if (el) {
            el.focus();
        }
    };

    useEffect(() => {
        const isAutofocus = !isMobile || isAndroid;

        if (isActive && isAutofocus) {
            if (isImperialHeight) {
                setTimeout(() => {
                    focusAndClick(ftInchesInputRefs.current[0]);
                }, 200);
            } else {
                setTimeout(() => {
                    focusAndClick(inputRef.current);
                }, 200);
            }
        }
    }, [isActive, isImperialHeight, unitType, isMobile, isAndroid]);

    const renderInput = () => {
        if (isImperialHeight) {
            return (
                <FeetInchesInput
                    refs={ftInchesInputRefs}
                    value={value}
                    onChange={setValue}
                    isActive={isActive}
                    onKeyDown={handleKeyDown}
                    className={inputClass}
                />
            );
        } else {
            return (
                <NumberInput
                    ref={inputRef}
                    type="number"
                    inputMode="decimal"
                    value={value && +value?.toFixed(0)}
                    placeholder={inputInfo?.placeHolder.toString() || agePlaceholder}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    className={inputClass}
                />
            );
        }
    };

    return (
        <div className="flex h-full flex-col justify-between pb-6 md:pb-10">
            <div className="relative grid gap-6">
                <div className="self-start text-zing-24-32 font-semibold text-white female:text-black">
                    {question.title}
                </div>
                <Badges
                    inputType={question.inputType as BodyMetricTypes}
                    onChange={handleUnitsChange}
                />
                <div className="grid">
                    {renderInput()}
                    {image && <img src={image} alt="image" className="mt-6" />}
                    {error && !(question.inputType === BodyMetricTypes.AGE) && (
                        <div className="mt-6 text-center text-zing-12-18 text-zing-warm-red-500">
                            {error}
                        </div>
                    )}
                </div>
                {(question.inputType !== BodyMetricTypes.WEIGHT || (!error && value)) && !image && (
                    <BodyMetricLabel
                        value={value}
                        questionId={question.id}
                        error={error || ageError}
                    />
                )}
            </div>
            {withConsent && (
                <ConsentComponent
                    canProceed={!!value && !error}
                    className="mt-auto pb-4 pt-4"
                    inputClass={inputClass}
                />
            )}
            <div className="sticky bottom-0 grid gap-2 bg-zing-bg-sticky-30 female:bg-zing-bg-sticky-30-white">
                <Button
                    onClick={handleContinue}
                    isDisabled={!value || !!error || !!ageError || !canProceed}
                    className="mx-auto"
                >
                    <span className="normal-case">{i18n.t("continue")}</span>
                </Button>
                {image && (
                    <Button
                        onClick={handleSkip}
                        className="female:text-zing-black mx-auto bg-transparent female:bg-transparent"
                    >
                        {i18n.t("dont_know")}
                    </Button>
                )}
            </div>
        </div>
    );
});

export default memo(DecimalInput);
